import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { RfqKycRequestDocument } from 'src/app/shared/services/rfq-kyc-request.service';

// =========================================================================
// Visual states a single document slot can occupy on the public KYC
// submission page. The parent page derives this from the slot model
// (uploadedFileUrl, the pending File pick and an in-flight upload flag).
// =========================================================================
export type KycTokenSlotState =
  | 'EMPTY'      // no picked file, no saved URL
  | 'PENDING'    // file picked, not yet flushed to S3
  | 'UPLOADING'  // presigned PUT in flight
  | 'UPLOADED'   // saved on the bundle, Preview + Replace available
  | 'REPLACING'; // user picked a new file over an uploaded one

/**
 * Slot view-model that the parent page hands to this child component.
 * The child stays presentational — it owns rendering and click handlers
 * but never mutates state directly. All changes round-trip back to the
 * parent via the output events.
 */
export interface KycTokenSlotVm {
  doc: RfqKycRequestDocument;

  // Local-only state — never persisted to the backend.
  pendingFile: File | null;
  state: KycTokenSlotState;

  // Inline error to surface under the picker (oversized file, wrong
  // mime, mid-upload failure). Parent clears this when a fresh pick
  // succeeds.
  errorMessage: string | null;
}

@Component({
  selector: 'app-kyc-token-slot',
  templateUrl: './kyc-token-slot.component.html',
  styleUrls: ['./kyc-token-slot.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KycTokenSlotComponent {

  constructor(private cdr: ChangeDetectorRef) {}

  @Input() slot!: KycTokenSlotVm;

  // Set by the parent during page-level SUBMITTING — disables every
  // interactive control on the slot so the supplier can't yank a file
  // out from under an in-flight submit.
  @Input() disabled = false;

  // ---- Events back to the parent page ---------------------------------

  // Supplier picked a file from the native dialog or via drag-and-drop.
  @Output() filePicked = new EventEmitter<File>();

  // Supplier cleared the pending pick (or the uploaded reference) and
  // wants the slot to return to EMPTY.
  @Output() fileRemoved = new EventEmitter<void>();

  // Supplier clicked Replace on an already-UPLOADED slot. Parent opens
  // the file picker; the resulting File flows through filePicked.
  @Output() fileReplaceRequested = new EventEmitter<void>();

  // Supplier clicked Preview on an UPLOADED slot. Parent opens the URL
  // in a new tab.
  @Output() filePreviewRequested = new EventEmitter<void>();

  // Supplier clicked Retry after a mid-upload network failure. Parent
  // re-runs the presigned PUT against the existing pendingFile.
  @Output() retryRequested = new EventEmitter<void>();

  // =====================================================================
  // Native-input handlers — kept tiny on purpose. All state lives in
  // the parent so the page can compute canSubmit() in one place.
  // =====================================================================

  // Local-only drag highlight. Pure presentational — the parent never
  // sees this flag; it just paints a deeper border on the dropzone
  // while a file is hovering over it.
  isDragOver = false;

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    // Reset the value so picking the same filename twice still fires
    // a change event.
    input.value = '';
    if (file) {
      this.filePicked.emit(file);
    }
  }

  // -------------------- Native drag-and-drop handlers ------------------
  // Wired on the dropzone (EMPTY state) and on the row chrome for
  // PENDING / UPLOADED states. A drop on a populated slot replaces the
  // current file — same behaviour as clicking Replace.

  onDragOver(event: DragEvent): void {
    if (this.disabled) return;
    // preventDefault is what tells the browser "this is a valid drop
    // target". Without it the drop event will never fire.
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    if (!this.isDragOver) {
      this.isDragOver = true;
      this.cdr.markForCheck();
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isDragOver) {
      this.isDragOver = false;
      this.cdr.markForCheck();
    }
  }

  onDrop(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.isDragOver) {
      this.isDragOver = false;
      this.cdr.markForCheck();
    }
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.filePicked.emit(files[0]);
    }
  }

  onRemoveClick(): void {
    if (this.disabled) return;
    this.fileRemoved.emit();
  }

  onReplaceClick(): void {
    if (this.disabled) return;
    this.fileReplaceRequested.emit();
  }

  onPreviewClick(): void {
    if (this.disabled) return;
    this.filePreviewRequested.emit();
  }

  onRetryClick(): void {
    if (this.disabled) return;
    this.retryRequested.emit();
  }

  // =====================================================================
  // Display helpers — pure derivations, no state mutation.
  // =====================================================================

  formatBytes(bytes: number | null | undefined): string {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  inputId(): string {
    // Unique per slot so the visible label/click target wires up to the
    // right hidden input. Critical because every slot has its own input
    // element on this page (no shared input).
    return `ktsFileInput-${this.slot.doc.id}`;
  }
}
